class CustomSearchAPI {
  constructor() {
    this.apiUrl = 'https://hob1.hobelieve.in/shopify_search/search';
    this.cache = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  async searchProducts(query) {
    // Check cache first
    const cacheKey = query.toLowerCase().trim();
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      return cached.data;
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: query })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // Extract product IDs from GIDs
        const productIds = result.data.map(gid => {
          const match = gid.match(/\/Product\/(\d+)$/);
          return match ? match[1] : null;
        }).filter(id => id !== null);

        // Cache the result with message
        this.cache.set(cacheKey, {
          data: productIds,
          message: result.message || '',
          timestamp: Date.now()
        });

        return { productIds, message: result.message || '' };
      } else {
        console.warn('API search returned no results or failed:', result);
        return [];
      }
    } catch (error) {
      console.error('Error calling search API:', error);
      return [];
    }
  }

  // Method to build search URL with product IDs using Shopify's native search
  buildFilteredSearchUrl(baseUrl, productIds, originalQuery) {
    const url = new URL(baseUrl, window.location.origin);
    
    if (productIds && productIds.length > 0) {
      // Create Shopify search query using ID syntax
      const idQuery = productIds.map(id => `id:${id}`).join(' OR ');
      
      url.searchParams.set('q', idQuery);
      url.searchParams.set('custom_search', 'true');
      url.searchParams.set('original_query', encodeURIComponent(originalQuery));
      
      console.log('Built search URL:', url.toString());
    } else {
      // No results from API, use original query
      url.searchParams.set('q', originalQuery);
    }
    
    return url.toString();
  }

  // Method to check if current page has custom search results
  hasCustomResults() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('custom_search') === 'true';
  }

  // Get original search query from URL
  getOriginalQuery() {
    const urlParams = new URLSearchParams(window.location.search);
    const originalQuery = urlParams.get('original_query');
    return originalQuery ? decodeURIComponent(originalQuery) : '';
  }

  // Get stored message from localStorage
  getStoredMessage() {
    const message = localStorage.getItem('customSearchMessage');
    const timestamp = localStorage.getItem('customSearchMessageTimestamp');
    
    if (message && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      // Return message if it's less than 5 minutes old
      if (age < 5 * 60 * 1000) {
        return message;
      }
    }
    
    return '';
  }

  // Clear stored search results (keeping for compatibility)
  clearStoredResults() {
    localStorage.removeItem('customSearchMessage');
    localStorage.removeItem('customSearchMessageTimestamp');
  }
}

// Global instance
window.CustomSearchAPI = new CustomSearchAPI();

// Enhanced form submission handler
function handleCustomSearchSubmission(form, isMainSearch = false) {
  const searchInput = form.querySelector('input[name="q"]');
  const query = searchInput?.value?.trim();
  
  if (!query) return true; // Let normal submission happen

  // Prevent default form submission and redirect immediately
  window.CustomSearchAPI.searchProducts(query)
    .then(result => {
      let productIds, message;
      
      // Handle both old format (array) and new format (object)
      if (Array.isArray(result)) {
        productIds = result;
        message = '';
      } else {
        productIds = result.productIds || [];
        message = result.message || '';
      }
      
      // Store message in localStorage for display on search results page
      if (message) {
        localStorage.setItem('customSearchMessage', message);
        localStorage.setItem('customSearchMessageTimestamp', Date.now().toString());
      }
      
      let targetUrl;
      
      if (isMainSearch) {
        // We're already on search results page, just update with new results
        targetUrl = window.CustomSearchAPI.buildFilteredSearchUrl(
          window.location.pathname, 
          productIds, 
          query
        );
      } else {
        // We're on custom search page, redirect to main search
        targetUrl = window.CustomSearchAPI.buildFilteredSearchUrl(
          '/search', 
          productIds, 
          query
        );
      }
      
      window.location.href = targetUrl;
    })
    .catch(error => {
      console.error('Search failed:', error);
      // Fall back to normal search
      form.submit();
    });

  return false; // Prevent normal form submission
}

// Add spinning animation CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);
